import * as React from 'react';

import type { PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';

import { ExploreCurriculum } from 'stile-shared/src/components/2024/common/ExploreCurriculum';
import { IconSet } from 'stile-shared/src/components/2024/common/IconSet/IconSet';
import { Link } from 'stile-shared/src/components/2024/common/Link';
import PedagogyHeader from 'stile-shared/src/components/2024/common/PedagogyHeader';
import PedagogyWithinStileCard from 'stile-shared/src/components/2024/common/PedagogyWithinStileCard';
import { SubNav, SubNavItem } from 'stile-shared/src/components/2024/common/SubNav';
import { Grid } from 'stile-shared/src/components/2024/layout/Grid';
import { Section } from 'stile-shared/src/components/2024/layout/Section';

import { PageLayout } from 'templates/2024/PageLayout';

const ExamplesSection = styled<typeof Section>(Section)`
  margin-top: 0 !important;
  grid-column: span 12;

  .content {
    padding-top: 0 !important;
    margin-bottom: 80px;
    @media (max-width: 768px) {
      padding-top: 0 !important;
      margin-bottom: 69px !important;
      margin: 0 25px;
    }
  }

  position: relative;

  h6 {
    color: var(--stile-green-3);
    margin: 0;
    margin-bottom: 10px;
    grid-column: span 12;
    @media (max-width: 768px) {
      margin-bottom: 0 !important;
    }
  }

  .grid-layout {
    margin-top: 0 !important;
    grid-column: 1 / span 12;
  }

  > .grid-layout {
    align-items: center;
    margin-bottom: 150px;
    @media (max-width: 768px) {
      margin-bottom: 80px;
    }
  }
`;

export { Head } from 'templates/2024/Head';

function UDLPage(props: PageProps) {
  const exploreCurriculumImage = (
    <StaticImage
      src="../../components/our-principles/images/teachersaying_quality_4.png"
      alt="Stacked documents with celestial diagram showing sun and orbiting planets"
      placeholder="blurred"
      layout="constrained"
      width={124}
      height={124}
    />
  );

  const subNavItems: SubNavItem[] = [
    {
      label: '5E Instructional Model',
      url: '/pedagogies/5e-instructional-model/',
    },
    {
      label: 'Phenomena-based Learning',
      url: '/pedagogies/phenomena-based-learning/',
    },
    {
      label: 'Three-dimensional Learning',
      url: '/pedagogies/three-dimensional-learning/',
    },
    {
      label: 'SOLO Taxonomy',
      url: '/pedagogies/solo-taxonomy/',
    },
    {
      label: 'Universal Design for Learning',
      url: '/pedagogies/universal-design/',
      isActive: true,
    },
    {
      label: 'Explicit Instruction',
      url: '/pedagogies/explicit-instruction/',
    },
    {
      label: 'Inquiry-based Learning',
      url: '/pedagogies/inquiry-based-learning/',
    },
    {
      label: 'Cognitive Load Theory',
      url: '/pedagogies/cognitive-load-theory/',
    },
    {
      label: 'Differentiated Instruction',
      url: '/pedagogies/differentiated-instruction/',
    },
  ];

  return (
    <PageLayout {...props}>
      <SubNav items={subNavItems} />

      <PedagogyHeader
        title="Universal design for learning"
        description="Universal Design for Learning is an educational framework that aims to improve and optimize teaching for all learners. It provides students with multiple means of engagement, representation, and expression to accommodate diverse learning styles, abilities, and backgrounds."
        iconBullets={[
          {
            iconSrc: IconSet['lightonIcon0404'],
            title: 'Initially developed by',
            text: 'David Rose & Anne Meyer at the Center for Applied Special Technology (CAST), Massachusetts, USA, 1990s.',
          },
          {
            iconSrc: IconSet['conflictIcon0505'],
            title: 'Effectiveness',
            text: (
              <>
                Among other effectiveness findings,{' '}
                <Link
                  url="https://www.tandfonline.com/doi/full/10.1080/13603116.2017.1325074"
                  openInNewTab
                >
                  Capp’s meta-analysis on the effectiveness of UDL
                </Link>{' '}
                showed that science content acquisition for students with disabilities improved
                substantially when using UDL, effect size of 0.62-0.79.
              </>
            ),
          },
          {
            iconSrc: IconSet['glossaryIcon0705'],
            title: 'Further reading',
            text: (
              <Link url="https://udlguidelines.cast.org/" openInNewTab>
                CAST UDL Guidelines
              </Link>
            ),
          },
          {
            iconSrc: IconSet['locationIcon0704'],
            title: "Where you'll find it in Stile",
            text: 'UDL principles are woven into all aspects Stile lessons.',
          },
        ]}
      />

      <ExamplesSection>
        <h6>Examples within Stile</h6>
        <Grid>
          <PedagogyWithinStileCard
            id="multi-modal-exposures"
            title="Multi-modal exposures"
            description="Scientific concepts and activities are communicated in short, succinct blocks of text, which can be narrated in Spanish. Text is accompanied by an illustrative picture. Videos and simulations are often used to accompany and reinforce concepts."
            url="http://stileapp.com/go/universal-example-1"
            subtitle={
              <div>
                <em>Sources of energy</em> lesson
                <br />
                in the Energy Conservation unit
              </div>
            }
            image={
              <StaticImage
                src="../../../../stile-shared/assets/images/pedagogies/udl_multi_modal_US.png"
                alt="Text: Solar, wind and hydropower title and summary text. Image: Lesson content about renewable energy with a photo of a hydroelectric dam and icons representing solar power, wind power, and hydropower."
                objectFit="contain"
              />
            }
          />
          <PedagogyWithinStileCard
            id="multiple-response-modes"
            title="Multiple response modes"
            description="Some open-ended questions allow students to respond in a way that makes sense to them. They can type, write, draw and even upload a video or audio recording."
            url="http://stileapp.com/go/universal-example-3"
            subtitle={
              <div>
                <em>Designing an earthquake-resistant building</em> lesson
                <br />
                in the Active Earth unit
              </div>
            }
            image={
              <StaticImage
                src="../../../../stile-shared/assets/images/pedagogies/udl_multi_response_US.png"
                alt="Text: Summarize the information you find using notes, diagrams, photos or videos. Image: Options for student responses including text response, files and media, YouTube, Vimeo, table, mind map, and graph."
                objectFit="contain"
              />
            }
          />
          <PedagogyWithinStileCard
            id="hands-on-learning"
            title="Hands-on learning"
            description="Stile units incorporate hands-on practical activities where students can engage with scientific phenomena first-hand. Classroom lessons include clear sections that indicate device-free learning opportunities and active engagement strategies."
            url="http://stileapp.com/go/universal-example-4"
            subtitle={
              <div>
                <em>Electric motors: How does an electric car work?</em> lesson
                <br />
                in the Energy unit
              </div>
            }
            image={
              <StaticImage
                src="../../../../stile-shared/assets/images/pedagogies/udl_hands_on_US.png"
                alt="Text: Notebooks. No laptops. Image: Student working on a science experiment with lab equipment, illustrated students writing in notebooks, and a box of Stile unit materials containing science supplies."
                objectFit="contain"
              />
            }
          />
          <PedagogyWithinStileCard
            id="interest-choice-branching"
            title="Interest-choice branching"
            description="Stile’s Engineering Challenges present students with a range of activity options so that they can pick and create a solution that motivates them."
            url="http://stileapp.com/go/universal-example-2"
            subtitle={
              <div>
                <em>Water wheels: Designing a solution</em> lesson
                <br />
                in the Energy unit
              </div>
            }
            image={
              <StaticImage
                src="../../../../stile-shared/assets/images/pedagogies/udl_interest.png"
                alt="Text: Redesign a water wheel using biomimicry, inspired by a plant or animal of your choice. Image: Photos of a pelican and a water plant with flowing water, alongside an illustration of a student designing a model water wheel."
                objectFit="contain"
              />
            }
          />
        </Grid>
      </ExamplesSection>

      <ExploreCurriculum
        title="Explore our core science curriculum"
        image={exploreCurriculumImage}
        exploreCardsData={[
          {
            title: "Inside Stile's Curriculum",
            description: 'How we make the best science lessons in the known universe.',
            url: '/what-is-stile/middle-school-curriculum/',
          },
          {
            title: "What's Included",
            description:
              'A complete, comprehensive curriculum — used, loved, and perfected by thousands of teachers.',
            url: '/what-is-stile/whats-included/',
          },
          {
            title: 'Why HQIM?',
            description: 'Why High-Quality Instructional Materials make all the difference.',
            url: '/why-choose-stile/high-quality-instructional-materials/',
          },
        ]}
      />
    </PageLayout>
  );
}

export default UDLPage;
